import React, { useEffect, useState } from "react";
import axiosInstance from '../api/axiosInstance';
//import "../styles/TableTracking.css";
import { FaChair, FaMobileAlt, FaEnvelope, FaTag, FaTimes, FaTrashAlt, FaCheck, FaExclamationTriangle } from 'react-icons/fa';

interface SessionDetail {
  tableId: number;
  sessionId: string;
  customerName: string;
  customerMobile: string;
  customerEmail: string;
  orderIds: string;
}

interface Table {
  id: number;
  restaurantGuid: string;
  tableLabel: string;
  seatCapacity?: string;
  isAvailable: boolean;
  originalStartTime?: string | Date | null;
  sessionDetails: SessionDetail[];
}

// Global toast function
export function showGlobalToast(message: string, type: 'success' | 'error' | 'celebration' = 'success') {
  window.dispatchEvent(new CustomEvent('global-toast', { detail: { message, type } }));
}

const TableTracking: React.FC = () => {
  const [tables, setTables] = useState<Table[]>([]);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [deleteSessionId, setDeleteSessionId] = useState<string | null>(null);
  const [remarks, setRemarks] = useState<string>("");
  const [showValidation, setShowValidation] = useState<boolean>(false);

  // Fetch tables
  const fetchTables = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get<Table[]>("/table/GetTableSessionDetails");
      setTables(response.data);
    } catch (error) {
      console.error("Error fetching table sessions:", error);
      showGlobalToast("Failed to fetch table sessions", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, []);

  // Validate remarks input
  const validateRemarks = (value: string) => {
    const sanitized = value.replace(/[^a-zA-Z0-9\s.,]/g, "");
    if (sanitized.length <= 200) setRemarks(sanitized);
    if (sanitized.trim().length > 0) setShowValidation(false);
  };

  // Delete session handler
  const handleDelete = async () => {
    if (!remarks.trim()) {
      setShowValidation(true);
      return;
    }
    if (!deleteSessionId) return;

    try {
      const response = await axiosInstance.delete(`/table/DeleteSession/${deleteSessionId}`, {
        data: { remarks }
      });
      const message = response.data?.message || "Session deleted successfully!";
      showGlobalToast(message, "success");
      fetchTables();
      setSelectedTable(null);
      setDeleteSessionId(null);
      setRemarks("");
      setShowValidation(false);
    } catch (error: any) {
      const message = error?.response?.data?.message || "Failed to delete session";
      console.error("Error deleting session:", error);
      showGlobalToast(message, "error");
    }
  };

  const getUsageDuration = (startTime: string | Date | null) => {
    if (!startTime) return null;

    const start = new Date(startTime).getTime();
    const now = Date.now();
    const diffMinutes = Math.floor((now - start) / 60000);

    let textColor = "#22c55e";   // Green
    let bgColor = "rgba(34,197,94,0.2)";
    let borderColor = "rgba(34,197,94,0.6)";
    let pulseAnimation = "none";

    if (diffMinutes > 60) {
      // 🔴 Urgent — fast pulse
      textColor = "#ef4444";
      bgColor = "rgba(239,68,68,0.25)";
      borderColor = "rgba(239,68,68,0.6)";
      pulseAnimation = "pulse-fast 0.6s infinite";
    } else if (diffMinutes > 45) {
      // 🟠 Moderate pulse
      textColor = "#f59e0b";
      bgColor = "rgba(245,158,11,0.25)";
      borderColor = "rgba(245,158,11,0.6)";
      pulseAnimation = "pulse-medium 1.2s infinite";
    } else if (diffMinutes > 30) {
      // 🟡 Soft pulse
      textColor = "#eab308";
      bgColor = "rgba(234,179,8,0.25)";
      borderColor = "rgba(234,179,8,0.5)";
      pulseAnimation = "pulse-soft 1.8s infinite";
    }

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    const formatted = hours > 0 ? `${hours} hr ${minutes} min` : `${minutes} min`;

    return { formatted, textColor, bgColor, borderColor, pulseAnimation };
  };


  const closeModal = () => {
    setSelectedTable(null);
    setDeleteSessionId(null);
    setRemarks("");
    setShowValidation(false);
  };

  return (
    <div className="w-full mx-auto p-4 sm:p-6 md:p-8 lg:p-10 bg-gray-100 text-gray-900 dark:bg-gray-900 dark:text-gray-100 min-h-full font-sans antialiased transition-colors duration-300">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-center mb-8 tracking-tight text-gray-900 dark:text-white">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-amber-600 dark:from-orange-400 dark:to-amber-500">
            Table Tracking
          </span>
        </h1>
        {loading && (
          <div className="flex justify-center items-center py-10">
            <svg className="animate-spin h-8 w-8 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="ml-4 text-lg font-medium text-gray-600 dark:text-gray-400">Loading tables...</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {tables.map((table) => (
            <div
              key={table.id}
              className={`p-6 rounded-3xl shadow-lg transform transition-all duration-300 ease-in-out cursor-pointer hover:scale-105 ${table.isAvailable
                ? 'bg-white dark:bg-gray-800'
                : 'bg-orange-50 dark:bg-gray-700 border-2 border-orange-400 dark:border-orange-500'
                }`}
              onClick={() => setSelectedTable(table)}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white truncate">{table.tableLabel}</h3>
                <span
                  className={`px-3 py-1 text-xs font-semibold rounded-full uppercase tracking-wider transition-colors duration-300 ${table.isAvailable
                    ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                    }`}
                >
                  {table.isAvailable ? 'Available' : 'Occupied'}
                </span>
              </div>
              <div className="flex items-center text-gray-600 dark:text-gray-400 mb-2">
                <FaChair className="mr-2 text-orange-500" />
                <p className="text-sm font-medium">Seats: {table.seatCapacity || 'N/A'}</p>
              </div>
              <div className="flex items-center text-gray-600 dark:text-gray-400">
                <FaTag className="mr-2 text-orange-500" />
                <p className="text-sm font-medium">Sessions: {table.sessionDetails.length}</p>
              </div>
              {/* Show duration only when table is Occupied */}
              {!table.isAvailable && table.originalStartTime && (() => {
                const usage = getUsageDuration(table.originalStartTime);
                return usage ? (
                  <div
                    className={`absolute bottom-2 right-2 px-3 py-1 text-xs font-semibold rounded-lg shadow-sm transition-all duration-300`}
                    style={{
                      color: usage.textColor,
                      backgroundColor: usage.bgColor,
                      border: `1px solid ${usage.borderColor}`,
                      backdropFilter: "blur(4px)",
                      animation: usage.pulseAnimation,
                    }}
                  >
                    ⏱ {usage.formatted}
                  </div>
                ) : null;
              })()}


            </div>
          ))}
        </div>

        {/* Session Details Modal */}
        {selectedTable && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-2xl max-w-lg w-full transform transition-all duration-300 scale-100 animate-slideUp overflow-hidden">
              <div className="flex justify-between items-start mb-6 border-b border-gray-200 dark:border-gray-700 pb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Sessions for {selectedTable.tableLabel}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <FaTimes size={20} />
                </button>
              </div>
              {selectedTable.sessionDetails.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8 text-lg font-medium">No active sessions for this table.</p>
              ) : (
                <ul className="space-y-6 max-h-80 overflow-y-auto pr-2 -mr-2 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800">
                  {selectedTable.sessionDetails.map((session) => (
                    <li key={session.sessionId} className="p-5 bg-gray-50 dark:bg-gray-700 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600 transition-shadow duration-200 hover:shadow-md">
                      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-3">
                        <p className="text-sm font-semibold text-orange-600 dark:text-orange-400 break-all mb-2 sm:mb-0">Session ID: <span className="text-gray-800 dark:text-gray-200 font-normal">{session.sessionId}</span></p>
                        <button
                          className="px-4 py-2 text-sm font-medium rounded-full bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200 flex items-center gap-1 w-full sm:w-auto justify-center"
                          onClick={() => setDeleteSessionId(session.sessionId)}
                        >
                          <FaTrashAlt size={12} />
                          Delete
                        </button>
                      </div>
                      <div className="space-y-2 text-gray-700 dark:text-gray-300">
                        <div className="flex items-center">
                          <FaTag className="mr-2 text-orange-500 dark:text-orange-400" />
                          <strong>Customer: </strong> {session.customerName}
                        </div>
                        <div className="flex items-center">
                          <FaMobileAlt className="mr-2 text-orange-500 dark:text-orange-400" />
                          <strong>Mobile: </strong> {session.customerMobile}
                        </div>
                        <div className="flex items-center">
                          <FaEnvelope className="mr-2 text-orange-500 dark:text-orange-400" />
                          <strong>Email: </strong> {session.customerEmail}
                        </div>
                        <div className="flex items-start">
                          <FaTag className="mr-2 mt-1 text-orange-500 dark:text-orange-400" />
                          <p className="truncate"><strong>Order IDs:</strong> {session.orderIds}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteSessionId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100 animate-slideUp">
              <div className="flex justify-between items-start mb-6 border-b border-gray-200 dark:border-gray-700 pb-4">
                <h2 className="text-2xl font-bold text-red-600 dark:text-red-400">Confirm Deletion</h2>
                <button
                  onClick={closeModal}
                  className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <FaTimes size={20} />
                </button>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Please enter a reason for deleting this session. This action cannot be undone.
              </p>
              <div className="mb-4">
                <label htmlFor="remarks" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Remarks (mandatory, max 200 chars, alphanumeric)
                </label>
                <textarea
                  id="remarks"
                  value={remarks}
                  onChange={(e) => validateRemarks(e.target.value)}
                  rows={3}
                  className={`w-full p-3 rounded-xl border-2 transition-colors duration-200 bg-gray-50 dark:bg-gray-700 dark:text-white ${showValidation ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-orange-500'
                    } focus:outline-none focus:ring-2`}
                  placeholder="e.g., Customer cancelled, incorrect booking, etc."
                />
                {showValidation && (
                  <div className="flex items-center mt-2 text-red-500 text-sm">
                    <FaExclamationTriangle className="mr-1" />
                    Remarks cannot be empty!
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row-reverse justify-end gap-3 mt-6">
                <button
                  onClick={handleDelete}
                  className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 text-lg font-semibold rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                >
                  <FaCheck size={16} /> Confirm
                </button>
                <button
                  onClick={closeModal}
                  className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 text-lg font-semibold rounded-full bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                >
                  <FaTimes size={16} /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableTracking;